import React, { useState } from 'react';
import { Grid, Paper, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Box, TextField, IconButton, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button, Switch } from '@mui/material';
import { Delete, AddCircleOutline, Edit, Check, Close, Print } from '@mui/icons-material';
import dayjs from 'dayjs';

type Pago = {
  observacion?: string;
  concepto?: string;
  moneda?: string; // 'USD' | 'ARS'
  importe?: number; // Monto en la moneda seleccionada
  tipoCambio?: number; // Tipo de cambio USD a ARS
  responsable_nombre?: string;
  revisado_por_nombre?: string;
  created_at?: string;
};

type FinancialSummaryProps = {
  precioVentaUSD: number;
  precioVentaARS: number;
  precioCostoUSD: number;
  precioCostoARS: number;
  formatearMoneda: (valor: number, moneda?: string) => string;
};

type PaymentsSectionProps = {
  precioVentaUSD: number;
  precioVentaARS: number;
  pagos: Pago[];
  nuevoPago: { concepto: string; moneda: string; monto: string; tipoCambio: string; responsable_nombre: string; revisado_por_nombre: string; };
  setNuevoPago: (pago: any) => void;
  agregarPago: () => void;
  editarPago: (index: number, pagoEditado: any) => void;
  eliminarPago: (index: number) => void;
  onToggleRevisadoPago?: (index: number, isChecked: boolean) => void;
  formatearMoneda: (valor: number, moneda?: string) => string;
  getCurrentUsername: () => string;
  paqueteId?: number;
};
// Componente para el Resumen Financiero — Moneda Dual
export const FinancialSummary: React.FC<FinancialSummaryProps> = ({ precioVentaUSD, precioVentaARS, precioCostoUSD, precioCostoARS, formatearMoneda }) => {
  const gananciaARS = precioVentaARS - precioCostoARS;
  const gananciaUSD = precioVentaUSD - precioCostoUSD;

  return (
    <Grid item xs={12} sx={{ mt: 2 }}>
      <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>RESUMEN FINANCIERO</Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Precio</Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {formatearMoneda(precioVentaARS, 'ARS')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 590, color: 'text.primary', opacity: 0.8 }}>
              {formatearMoneda(precioVentaUSD, 'USD')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Costo</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5, color: 'error.main' }}>
                {formatearMoneda(precioCostoARS, 'ARS')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 590, color: 'error.main', opacity: 0.8 }}>
                {formatearMoneda(precioCostoUSD, 'USD')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Ganancia</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5, color: 'success.main' }}>
                {formatearMoneda(gananciaARS, 'ARS')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight:590, color: 'success.main', opacity: 0.8 }}>
                {formatearMoneda(gananciaUSD, 'USD')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

// Componente para el Listado de Pagos — Moneda Dual
export const PaymentsSection: React.FC<PaymentsSectionProps> = ({ 
  precioVentaUSD,
  precioVentaARS,
  pagos = [], 
  nuevoPago, 
  setNuevoPago, 
  agregarPago, 
  editarPago,
  eliminarPago, 
  onToggleRevisadoPago,
  formatearMoneda,
  getCurrentUsername,
  paqueteId
}) => {
  const [editando, setEditando] = useState<number | null>(null);
  const [pagoEditado, setPagoEditado] = useState<any>(null);
  const [dialogoEliminar, setDialogoEliminar] = useState(false);
  const [pagoAEliminar, setPagoAEliminar] = useState<{ index: number; concepto: string } | null>(null);

  // --- RBAC: determinar si el usuario es admin ---
  const isAdmin = React.useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u.rol === "Superadministrador" || u.rol === "Administrador";
    } catch { return false; }
  }, []);

  const handleEditarClick = (index: number) => {
    const pago = pagos[index];
    setEditando(index);
    setPagoEditado({
      observacion: pago.observacion || '',
      moneda: pago.moneda || 'USD',
      importe: pago.importe || 0,
      tipoCambio: tieneTCReal(pago.tipoCambio) ? String(Number(pago.tipoCambio)) : '',
      responsable_nombre: (pago as any).responsable_nombre || '',
      revisado_por_nombre: (pago as any).revisado_por_nombre || '',
    });
  };

  const handleCancelarEdicion = () => {
    setEditando(null);
    setPagoEditado(null);
  };

  const handleGuardarEdicion = () => {
    if (editando === null || !pagoEditado) return;
    
    const pagoData = {
      observacion: pagoEditado.observacion,
      moneda: pagoEditado.moneda,
      importe: parseFloat(pagoEditado.importe),
      tipoCambio: pagoEditado.tipoCambio ? parseFloat(pagoEditado.tipoCambio) : null,
      responsable_nombre: pagoEditado.responsable_nombre || '',
      revisado_por_nombre: pagoEditado.revisado_por_nombre || '',
    };

    editarPago(editando, pagoData);
    setEditando(null);
    setPagoEditado(null);
  };

  const handleEliminarClick = (index: number) => {
    const pago = pagos[index];
    setPagoAEliminar({ index, concepto: pago.observacion || 'Sin concepto' });
    setDialogoEliminar(true);
  };

  const confirmarEliminar = () => {
    if (pagoAEliminar !== null) {
      eliminarPago(pagoAEliminar.index);
    }
    setDialogoEliminar(false);
    setPagoAEliminar(null);
  };

  const cancelarEliminar = () => {
    setDialogoEliminar(false);
    setPagoAEliminar(null);
  };

  // Helper: ¿tiene tipo de cambio real? (distinto de null, 0, 1)
  const tieneTCReal = (tc: any) => {
    const val = Number(tc);
    return val > 0 && val !== 1;
  };

  // Calcular total pagado separado por moneda
  // Si un pago tiene tipoCambio real, se convierte a la moneda OPUESTA
  const calcularPagadoUSD = () => {
    return (pagos || []).reduce((acc, p) => {
      const monedaPago = ((p as any).moneda || 'USD').toUpperCase();
      const tc = (p as any).tipoCambio;
      const importe = Number((p as any).importe || 0);

      if (monedaPago === 'USD' && !tieneTCReal(tc)) {
        // Pago directo en USD sin conversión
        return acc + importe;
      }
      if (monedaPago === 'ARS' && tieneTCReal(tc)) {
        // Pago en ARS con TC → convierte a USD (importe / TC)
        return acc + (importe / Number(tc));
      }
      return acc;
    }, 0);
  };

  const calcularPagadoARS = () => {
    return (pagos || []).reduce((acc, p) => {
      const monedaPago = ((p as any).moneda || 'USD').toUpperCase();
      const tc = (p as any).tipoCambio;
      const importe = Number((p as any).importe || 0);

      if (monedaPago === 'ARS' && !tieneTCReal(tc)) {
        // Pago directo en ARS sin conversión
        return acc + importe;
      }
      if (monedaPago === 'USD' && tieneTCReal(tc)) {
        // Pago en USD con TC → convierte a ARS (importe * TC)
        return acc + (importe * Number(tc));
      }
      return acc;
    }, 0);
  };

  // Helper para mostrar equivalencia de un pago con TC
  const getEquivalencia = (p: Pago) => {
    const tc = (p as any).tipoCambio;
    if (!tieneTCReal(tc)) return null;
    const monedaPago = ((p as any).moneda || 'USD').toUpperCase();
    const importe = Number((p as any).importe || 0);
    if (monedaPago === 'ARS') {
      return { monto: importe / Number(tc), moneda: 'USD' };
    } else {
      return { monto: importe * Number(tc), moneda: 'ARS' };
    }
  };

  const pagadoUSD = calcularPagadoUSD();
  const pagadoARS = calcularPagadoARS();
  const saldoUSD = (precioVentaUSD || 0) - pagadoUSD;
  const saldoARS = (precioVentaARS || 0) - pagadoARS;

  return (
    <Grid item xs={12}>
      <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>LISTADO DE PAGOS</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center', width: '4%' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '10%' }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '18%' }}>Concepto</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '7%' }}>Moneda</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '12%' }}>Importe</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '10%' }}>T. Cambio</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '12%' }}>Equivale</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '12%' }}>Responsable</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '10%', textAlign: 'center' }}>Revisado</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '7%' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(pagos || []).map((p: Pago, idx: number) => (
                <TableRow key={idx}>
                  <TableCell sx={{ textAlign: 'center' }}>{idx + 1}</TableCell>
                  <TableCell>
                    {p.created_at ? dayjs(p.created_at).format("DD/MM/YYYY") : '—'}
                  </TableCell>
                  <TableCell>
                    {editando === idx ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={pagoEditado.observacion}
                        onChange={(e) => setPagoEditado({ ...pagoEditado, observacion: e.target.value })}
                      />
                    ) : (
                      p.observacion || '-'
                    )}
                  </TableCell>

                  <TableCell>
                    {editando === idx ? (
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={pagoEditado.moneda}
                        onChange={(e) => setPagoEditado({ ...pagoEditado, moneda: e.target.value })}
                      >
                        <MenuItem value="USD">USD</MenuItem>
                        <MenuItem value="ARS">ARS</MenuItem>
                      </TextField>
                    ) : (
                      p.moneda || 'USD'
                    )}
                  </TableCell>
                  <TableCell>
                    {editando === idx ? (
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        value={pagoEditado.importe}
                        onChange={(e) => setPagoEditado({ ...pagoEditado, importe: e.target.value })}
                      />
                    ) : (
                      formatearMoneda(Number(p.importe || 0), p.moneda || 'USD')
                    )}
                  </TableCell>
                  {/* T. Cambio */}
                  <TableCell>
                    {editando === idx ? (
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        placeholder="Opcional"
                        inputProps={{ style: { fontSize: '0.85rem' } }}
                        value={pagoEditado.tipoCambio}
                        onChange={(e) => setPagoEditado({ ...pagoEditado, tipoCambio: e.target.value })}
                      />
                    ) : (
                      tieneTCReal((p as any).tipoCambio) ? Number((p as any).tipoCambio).toLocaleString('es-AR') : '-'
                    )}
                  </TableCell>
                  {/* Equivale — dinámico en edición */}
                  <TableCell>
                    {(() => {
                      if (editando === idx && pagoEditado) {
                        const tc = parseFloat(pagoEditado.tipoCambio);
                        const monto = parseFloat(pagoEditado.importe);
                        if (!tc || tc <= 0 || tc === 1 || !monto) return '-';
                        const moneda = pagoEditado.moneda === 'ARS' ? 'USD' : 'ARS';
                        const equiv = pagoEditado.moneda === 'ARS' ? monto / tc : monto * tc;
                        return <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>{formatearMoneda(equiv, moneda)}</Typography>;
                      }
                      const eq = getEquivalencia(p);
                      if (!eq) return '-';
                      return <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>{formatearMoneda(eq.monto, eq.moneda)}</Typography>;
                    })()}
                  </TableCell>
                  <TableCell>
                    {editando === idx ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={pagoEditado?.responsable_nombre || ''}
                        disabled
                        sx={{ 
                          '& .MuiInputBase-input.Mui-disabled': { 
                            color: 'text.primary',
                            WebkitTextFillColor: 'text.primary'
                          } 
                        }}
                      />
                    ) : (
                      (p as any).responsable_nombre || '-'
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Switch
                      checked={!!(p as any).revisado_por_nombre}
                      onChange={(e) => onToggleRevisadoPago && onToggleRevisadoPago(idx, e.target.checked)}
                      size="small"
                      disabled={editando === idx || !isAdmin}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {editando === idx ? (
                      <>
                        <IconButton size="small" onClick={handleGuardarEdicion} color="success">
                          <Check fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={handleCancelarEdicion} color="default">
                          <Close fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton size="small" onClick={() => handleEditarClick(idx)} color="primary">
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleEliminarClick(idx)} color="error">
                          <Delete fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {(pagos || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography color="text.secondary">No hay pagos registrados</Typography>
                  </TableCell>
                </TableRow>
              )}
              {/* Fila para agregar nuevo pago */}
              <TableRow>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">-</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{dayjs().format("DD/MM/YYYY")}</Typography>
                </TableCell>

                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Concepto"
                    value={nuevoPago.concepto}
                    onChange={(e) => setNuevoPago({ ...nuevoPago, concepto: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={nuevoPago.moneda}
                    onChange={(e) => setNuevoPago({ ...nuevoPago, moneda: e.target.value })}
                  >
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="ARS">ARS</MenuItem>
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    placeholder={`Importe ${nuevoPago.moneda === 'ARS' ? '$' : 'U$D'}`}
                    value={nuevoPago.monto}
                    onChange={(e) => setNuevoPago({ ...nuevoPago, monto: e.target.value })}
                  />
                </TableCell>
                {/* T. Cambio nuevo pago */}
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    placeholder="Opcional"
                    inputProps={{ style: { fontSize: '0.85rem' } }}
                    value={nuevoPago.tipoCambio}
                    onChange={(e) => setNuevoPago({ ...nuevoPago, tipoCambio: e.target.value })}
                  />
                </TableCell>
                {/* Equivale nuevo pago */}
                <TableCell>
                  {(() => {
                    const tc = parseFloat(nuevoPago.tipoCambio);
                    const monto = parseFloat(nuevoPago.monto);
                    if (!tc || tc <= 0 || tc === 1 || !monto) return '-';
                    const moneda = nuevoPago.moneda === 'ARS' ? 'USD' : 'ARS';
                    const equiv = nuevoPago.moneda === 'ARS' ? monto / tc : monto * tc;
                    return <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>{formatearMoneda(equiv, moneda)}</Typography>;
                  })()}
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Responsable"
                    value={nuevoPago.responsable_nombre || getCurrentUsername()}
                    disabled
                    sx={{ 
                      '& .MuiInputBase-input.Mui-disabled': { 
                        color: 'text.primary',
                        WebkitTextFillColor: 'text.primary'
                      } 
                    }}
                  />
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Switch
                    checked={false}
                    disabled
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton 
                    size="small" 
                    color="success" 
                    onClick={agregarPago} 
                    disabled={!nuevoPago.concepto || !nuevoPago.monto}
                    title="Guardar pago"
                  >
                    <AddCircleOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Resumen de Pagos — Doble Moneda */}
        <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography align="center" variant="body2">A Pagar</Typography>
              <Typography align="center" variant="h6" sx={{ fontWeight: 600 }}>{formatearMoneda(precioVentaARS, 'ARS')}</Typography>
              <Typography align="center" variant="h6" sx={{ fontWeight: 600, opacity: 0.8 }}>{formatearMoneda(precioVentaUSD, 'USD')}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography align="center" variant="body2">Pagado</Typography>
              <Typography align="center" variant="h6" sx={{ fontWeight: 600 }}>{formatearMoneda(pagadoARS, 'ARS')}</Typography>
              <Typography align="center" variant="h6" sx={{ fontWeight: 600, opacity: 0.8 }}>{formatearMoneda(pagadoUSD, 'USD')}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography align="center" variant="body2">Saldo</Typography>
              <Typography align="center" variant="h6" sx={{ fontWeight: 600 }} color={saldoARS > 0 ? 'error' : 'success.main'}>{formatearMoneda(saldoARS, 'ARS')}</Typography>
              <Typography align="center" variant="h6" sx={{ fontWeight: 600, opacity: 0.8 }} color={saldoUSD > 0 ? 'error' : 'success.main'}>{formatearMoneda(saldoUSD, 'USD')}</Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Botón Imprimir */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={() => {
              const totalUSD = pagadoUSD;
              const totalARS = pagadoARS;
              
              const filas = (pagos || []).map((p, i) => {
                const eq = getEquivalencia(p);
                const tcVal = tieneTCReal((p as any).tipoCambio) ? Number((p as any).tipoCambio).toLocaleString('es-AR') : '-';
                const eqStr = eq ? formatearMoneda(eq.monto, eq.moneda) : '-';
                const fechaStr = p.created_at ? dayjs(p.created_at).format("DD/MM/YYYY") : '—';
                return `
                <tr style="border-bottom: 1px solid #ddd;">
                  <td style="padding: 8px; text-align: center;">${i + 1}</td>
                  <td style="padding: 8px; text-align: center;">${fechaStr}</td>
                  <td style="padding: 8px;">${p.observacion || '-'}</td>
                  <td style="padding: 8px;">${p.moneda || 'USD'}</td>
                  <td style="padding: 8px; text-align: right;">${formatearMoneda(Number(p.importe || 0), p.moneda || 'USD')}</td>
                  <td style="padding: 8px; text-align: right;">${tcVal}</td>
                  <td style="padding: 8px; text-align: right; font-style: italic; color: #666;">${eqStr}</td>
                </tr>`;
              }).join('');
              
              const html = `
                <!DOCTYPE html>
                <html>
                <head>
                  <title>Listado de Pagos - Paquete #${paqueteId || ''}</title>
                  <style>
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    .header { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; border-bottom: 2px solid #461264; padding-bottom: 15px; }
                    .logo { height: 50px; }
                    .titulo { flex: 1; }
                    .titulo h2 { margin: 0; color: #461264; }
                    .titulo p { margin: 5px 0 0 0; color: #666; }
                    table { width: 100%; border-collapse: collapse; margin-top: 15px; }
                    th { background: #f5f5f5; padding: 10px; text-align: left; border-bottom: 2px solid #333; }
                    th:first-child { width: 10%; text-align: center; }
                    th:last-child { text-align: right; width: 25%; }
                    tfoot td { font-weight: bold; border-top: 2px solid #333; padding: 10px; }
                  </style>
                </head>
                <body>
                  <div class="header">
                    <img src="${window.location.origin}/logo.png" alt="Logo" class="logo" />
                    <div class="titulo">
                      <h2>LISTADO DE PAGOS</h2>
                      <p>Paquete #${paqueteId || 'N/A'}</p>
                    </div>
                  </div>
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Fecha</th>
                        <th>Concepto</th>
                        <th>Moneda</th>
                        <th>Importe</th>
                        <th>T. Cambio</th>
                        <th>Equivale</th>
                      </tr>
                    </thead>
                    <tbody>${filas}</tbody>
                    <tfoot>
                      <tr>
                        <td colspan="4" style="text-align: right;">TOTAL ARS:</td>
                        <td style="text-align: right;">${formatearMoneda(totalARS, 'ARS')}</td>
                      </tr>
                      <tr>
                        <td colspan="4" style="text-align: right;">TOTAL USD:</td>
                        <td style="text-align: right;">${formatearMoneda(totalUSD, 'USD')}</td>
                      </tr>
                    </tfoot>
                  </table>

                  <div style="margin-top: 25px; padding: 15px 0; border-top: 2px solid #461264;">
                    <table style="width: 100%; border-collapse: collapse;">
                      <tr>
                        <td style="width: 33%; text-align: center; padding: 10px; vertical-align: top;">
                          <div style="font-size: 12px; color: #666; margin-bottom: 5px;">A Pagar</div>
                          <div style="font-size: 16px; font-weight: bold;">${formatearMoneda(precioVentaARS, 'ARS')}</div>
                          <div style="font-size: 16px; font-weight: bold; opacity: 0.8;">${formatearMoneda(precioVentaUSD, 'USD')}</div>
                        </td>
                        <td style="width: 33%; text-align: center; padding: 10px; vertical-align: top; border-left: 1px solid #ddd; border-right: 1px solid #ddd;">
                          <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Pagado</div>
                          <div style="font-size: 16px; font-weight: bold;">${formatearMoneda(pagadoARS, 'ARS')}</div>
                          <div style="font-size: 16px; font-weight: bold; opacity: 0.8;">${formatearMoneda(pagadoUSD, 'USD')}</div>
                        </td>
                        <td style="width: 33%; text-align: center; padding: 10px; vertical-align: top;">
                          <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Saldo</div>
                          <div style="font-size: 16px; font-weight: bold; color: ${saldoARS > 0 ? '#d32f2f' : '#2e7d32'};">${formatearMoneda(saldoARS, 'ARS')}</div>
                          <div style="font-size: 16px; font-weight: bold; opacity: 0.8; color: ${saldoUSD > 0 ? '#d32f2f' : '#2e7d32'};">${formatearMoneda(saldoUSD, 'USD')}</div>
                        </td>
                      </tr>
                    </table>
                  </div>
                </body>
                </html>
              `;
              
              const win = window.open('', '_blank');
              if (win) {
                win.document.write(html);
                win.document.close();
                win.focus();
                setTimeout(() => win.print(), 250);
              }
            }}
            disabled={!pagos || pagos.length === 0}
          >
            Imprimir
          </Button>
        </Box>

        {/* Dialog de confirmación para eliminar */}
        <Dialog open={dialogoEliminar} onClose={cancelarEliminar}>
          <DialogTitle>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Estás seguro de que deseas eliminar el pago "{pagoAEliminar?.concepto}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelarEliminar}>Cancelar</Button>
            <Button onClick={confirmarEliminar} color="error" variant="contained">
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Grid>
  );
};
